import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { organizationManagerRepo } from "@repositories";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState, type FC } from "react";
import { useAlert } from "../../../contexts/AlertProvider";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useUser } from "../../../contexts/UserProvider";
import {
  addManualMatch,
  assignManualMatchDate,
  fetchManagerDailyMatches,
  fetchMatchBank,
} from "../managerDailyMatchesApi";
import type { MatchBankEntry } from "../types";
import {
  StatisticsDailyLineChart,
  type DailyChartRow,
} from "./StatisticsDailyLineChart";

function defaultFrom(): Dayjs {
  return dayjs().subtract(9, "day").startOf("day");
}

function defaultTo(): Dayjs {
  return dayjs().startOf("day");
}

const chartCardSx = {
  minWidth: 0,
  p: 2,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
} as const;

export const ManagerDailyMatchesSection: FC = () => {
  const { currOrganization } = useOrganization();
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  const { setAlertText, setSeverity } = useAlert();
  const [from, setFrom] = useState<Dayjs>(defaultFrom);
  const [to, setTo] = useState<Dayjs>(defaultTo);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualDate, setManualDate] = useState<Dayjs>(dayjs().startOf("day"));
  const [addToBank, setAddToBank] = useState(true);
  const [manualManagerId, setManualManagerId] = useState<string>("");
  const [manualCount, setManualCount] = useState("1");
  const [manualNote, setManualNote] = useState("");
  const [assignEntry, setAssignEntry] = useState<MatchBankEntry | null>(null);
  const [assignDate, setAssignDate] = useState<Dayjs>(dayjs().startOf("day"));
  const [assignCount, setAssignCount] = useState("1");

  const { dateFrom, dateTo } = useMemo(() => {
    let a = from.startOf("day");
    let b = to.startOf("day");
    if (a.isAfter(b)) {
      const t = a;
      a = b;
      b = t;
    }
    return {
      dateFrom: a.format("YYYY-MM-DD"),
      dateTo: b.format("YYYY-MM-DD"),
    };
  }, [from, to]);

  const { data, isLoading } = useQuery({
    queryKey: [
      "statistics-manager-daily-matches",
      currOrganization?.id,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      fetchManagerDailyMatches({
        organizationId: currOrganization!.id,
        dateFrom,
        dateTo,
      }),
    enabled: !!currOrganization?.id,
    staleTime: 1000 * 60 * 2,
  });

  const canManageManualMatches = data?.canManageManualMatches ?? false;

  const { data: bankEntries = [], isLoading: bankLoading } = useQuery({
    queryKey: ["statistics-match-bank", currOrganization?.id],
    queryFn: () => fetchMatchBank(currOrganization!.id),
    enabled: !!currOrganization?.id && canManageManualMatches,
    staleTime: 1000 * 30,
  });

  const { data: orgManagers = [] } = useQuery({
    queryKey: ["manual-match-org-managers", currOrganization?.id],
    queryFn: () =>
      organizationManagerRepo.find({
        where: { organization: { $id: currOrganization!.id } },
        orderBy: { name: "asc" },
        select: { id: true, name: true },
      }),
    enabled: manualDialogOpen && !!currOrganization?.id,
  });

  const [selectedManagerId, setSelectedManagerId] = useState<string>("");

  const managers = data?.managers ?? [];
  const canViewAllManagers = data?.canViewAllManagers ?? false;

  const activeManagerId = useMemo(() => {
    if (!managers.length) return "";
    if (
      selectedManagerId &&
      managers.some((m) => m.managerId === selectedManagerId)
    ) {
      return selectedManagerId;
    }
    return managers[0].managerId;
  }, [managers, selectedManagerId]);

  const activeManager = managers.find((m) => m.managerId === activeManagerId);

  const { chartData, rangeTotal } = useMemo(() => {
    const empty: DailyChartRow[] = [];
    if (!activeManager?.days.length) {
      return { chartData: empty, rangeTotal: 0 };
    }
    let total = 0;
    const rows: DailyChartRow[] = activeManager.days.map((row) => {
      total += row.matchCount;
      return {
        dayLabel: dayjs(row.date).format("DD/MM"),
        value: row.matchCount,
      };
    });
    return { chartData: rows, rangeTotal: total };
  }, [activeManager]);

  const invalidateMatchQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["statistics-manager-daily-matches"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["statistics-match-bank"],
      }),
    ]);
  };

  const addManualMutation = useMutation({
    mutationFn: addManualMatch,
    onSuccess: async (_data, variables) => {
      setManualDialogOpen(false);
      setManualNote("");
      setManualCount("1");
      setAddToBank(true);
      setManualManagerId("");
      setSelectedManagerId(variables.createdByManagerId || currentUser?.id || "");
      setAlertText(
        variables.addToBank ? "נוסף לבנק המאצ׳ים" : "מאצ' ידני נוסף בהצלחה",
      );
      setSeverity("success");
      await invalidateMatchQueries();
    },
    onError: () => {
      setAlertText("לא ניתן להוסיף מאצ' ידני");
      setSeverity("error");
    },
  });

  const assignDateMutation = useMutation({
    mutationFn: assignManualMatchDate,
    onSuccess: async () => {
      setAssignEntry(null);
      setAlertText("המאצ׳ שויך לתאריך");
      setSeverity("success");
      await invalidateMatchQueries();
    },
    onError: () => {
      setAlertText("לא ניתן לשייך לתאריך");
      setSeverity("error");
    },
  });

  const handleSubmitManualMatch = () => {
    if (!currOrganization?.id) return;
    const count = Number.parseInt(manualCount, 10);
    if (!Number.isFinite(count) || count < 1) {
      setAlertText("יש להזין כמות של לפחות 1");
      setSeverity("warning");
      return;
    }
    addManualMutation.mutate({
      organizationId: currOrganization.id,
      matchCount: count,
      addToBank,
      matchedAt: addToBank ? undefined : manualDate.format("YYYY-MM-DD"),
      createdByManagerId: manualManagerId || undefined,
      note: manualNote.trim() || undefined,
    });
  };

  const sectionTitle = canViewAllManagers
    ? "מאצ׳ים יומיים לפי מנהל"
    : "המאצ׳ים שלי (יומי)";
  const managerOptions = useMemo(() => {
    const fromQuery = orgManagers.map((m) => ({
      managerId: m.id,
      managerName: m.name,
    }));
    if (fromQuery.length > 0) return fromQuery;
    return managers;
  }, [orgManagers, managers]);

  const bankTotal = bankEntries.reduce((sum, e) => sum + e.matchCount, 0);

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h6" fontWeight={600} color="primary">
          {sectionTitle}
        </Typography>
        {canManageManualMatches && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setManualDialogOpen(true)}
          >
            הוסף מאצ' ידני / לבנק
          </Button>
        )}
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        flexWrap="wrap"
      >
        <DatePicker
          label="מתאריך"
          value={from}
          onChange={(v) => v && setFrom(v.startOf("day"))}
          maxDate={dayjs()}
          slotProps={{
            textField: { size: "small" },
            popper: { className: "date-picker-dialog" },
            layout: { className: "date-field-ltr" },
          }}
        />
        <DatePicker
          label="עד תאריך"
          value={to}
          onChange={(v) => v && setTo(v.startOf("day"))}
          maxDate={dayjs()}
          slotProps={{
            textField: { size: "small" },
            popper: { className: "date-picker-dialog" },
            layout: { className: "date-field-ltr" },
          }}
        />
        {canViewAllManagers && managers.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="manager-matches-select-label">מנהל</InputLabel>
            <Select
              labelId="manager-matches-select-label"
              label="מנהל"
              value={activeManagerId}
              onChange={(e) => setSelectedManagerId(e.target.value)}
            >
              {managers.map((manager) => (
                <MenuItem key={manager.managerId} value={manager.managerId}>
                  {manager.managerName} ({manager.total})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ maxWidth: 420 }}
        >
          מאצ'ים לפי גודל קבוצה בזמן אמת. מאצ'ים ידניים משויכים למנהל שנבחר.
          בנק מאצ׳ים נשמר בלי תאריך וניתן לשייך בחלקים לתאריכים שונים. היתרה נשארת בבנק.
        </Typography>
      </Stack>

      {canViewAllManagers && managers.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {managers.map((manager) => (
            <Typography
              key={manager.managerId}
              variant="body2"
              sx={{
                px: 1.5,
                py: 0.75,
                borderRadius: 1,
                bgcolor:
                  manager.managerId === activeManagerId
                    ? "primary.main"
                    : "action.hover",
                color:
                  manager.managerId === activeManagerId
                    ? "primary.contrastText"
                    : "text.primary",
                cursor: managers.length > 1 ? "pointer" : "default",
              }}
              onClick={() => setSelectedManagerId(manager.managerId)}
            >
              {manager.managerName}: {manager.total}
            </Typography>
          ))}
        </Stack>
      )}

      <Paper elevation={1} sx={chartCardSx}>
        <StatisticsDailyLineChart
          title={
            canViewAllManagers && activeManager
              ? `מאצ׳ים יומיים - ${activeManager.managerName}`
              : "מאצ׳ים יומיים"
          }
          color="#7b2cbf"
          data={chartData}
          isLoading={isLoading}
          xUnit="יום"
          rangeTotal={rangeTotal}
        />
      </Paper>

      {canManageManualMatches && (
        <Paper elevation={1} sx={chartCardSx}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography variant="subtitle1" fontWeight={600}>
              בנק מאצ׳ים
            </Typography>
            <Typography variant="body2" color="text.secondary">
              סה״כ בבנק: {bankTotal}
            </Typography>
          </Stack>
          {bankLoading ? (
            <Typography variant="body2" color="text.secondary">
              טוען…
            </Typography>
          ) : bankEntries.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              אין מאצ׳ים בבנק. הוסיפו מאצ׳ ידני עם סימון ״הוסף לבנק״.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>כמות</TableCell>
                  <TableCell>מנהל</TableCell>
                  <TableCell>הערה</TableCell>
                  <TableCell>נוצר</TableCell>
                  <TableCell align="center">פעולה</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bankEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{entry.matchCount}</TableCell>
                    <TableCell>
                      {entry.createdByManagerName ?? "-"}
                    </TableCell>
                    <TableCell>{entry.note || "-"}</TableCell>
                    <TableCell>
                      {dayjs(entry.createdAt).format("DD/MM/YYYY")}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setAssignEntry(entry);
                          setAssignDate(dayjs().startOf("day"));
                          setAssignCount(String(entry.matchCount));
                        }}
                      >
                        שייך לתאריך
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      )}

      <Dialog
        open={manualDialogOpen}
        onClose={() => setManualDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>הוספת מאצ' ידני</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={addToBank}
                  onChange={(e) => setAddToBank(e.target.checked)}
                />
              }
              label="הוסף לבנק מאצ׳ים (ללא תאריך)"
            />
            <DatePicker
              label="תאריך המאצ'"
              value={manualDate}
              onChange={(v) => v && setManualDate(v.startOf("day"))}
              maxDate={dayjs()}
              disabled={addToBank}
              slotProps={{
                textField: { size: "small", fullWidth: true },
                popper: { className: "date-picker-dialog" },
                layout: { className: "date-field-ltr" },
              }}
            />
            {canViewAllManagers && (
              <FormControl size="small" fullWidth>
                <InputLabel id="manual-match-manager-label">שיוך למנהל</InputLabel>
                <Select
                  labelId="manual-match-manager-label"
                  label="שיוך למנהל"
                  value={manualManagerId}
                  onChange={(e) => setManualManagerId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>לי (ברירת מחדל)</em>
                  </MenuItem>
                  {managerOptions.map((manager) => (
                    <MenuItem key={manager.managerId} value={manager.managerId}>
                      {manager.managerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="כמות מאצ'ים"
              type="number"
              size="small"
              fullWidth
              value={manualCount}
              onChange={(e) => setManualCount(e.target.value)}
              inputProps={{ min: 1 }}
            />
            <TextField
              label="הערה (אופציונלי)"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={manualNote}
              onChange={(e) => setManualNote(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualDialogOpen(false)}>ביטול</Button>
          <Button
            variant="contained"
            onClick={handleSubmitManualMatch}
            disabled={addManualMutation.isPending}
          >
            שמירה
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!assignEntry}
        onClose={() => setAssignEntry(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>שיוך מאצ׳ לתאריך</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {assignEntry?.note ? assignEntry.note : "שיוך חלקי או מלא מהבנק"}
            </Typography>
            <TextField
              label={`כמות מאצ'ים (מתוך ${assignEntry?.matchCount ?? 0} בבנק)`}
              type="number"
              size="small"
              fullWidth
              value={assignCount}
              onChange={(e) => setAssignCount(e.target.value)}
              inputProps={{ min: 1, max: assignEntry?.matchCount ?? 1 }}
            />
            <DatePicker
              label="תאריך"
              value={assignDate}
              onChange={(v) => v && setAssignDate(v.startOf("day"))}
              maxDate={dayjs()}
              slotProps={{
                textField: { size: "small", fullWidth: true },
                popper: { className: "date-picker-dialog" },
                layout: { className: "date-field-ltr" },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignEntry(null)}>ביטול</Button>
          <Button
            variant="contained"
            disabled={!assignEntry || assignDateMutation.isPending}
            onClick={() => {
              if (!currOrganization?.id || !assignEntry) return;
              const count = Number.parseInt(assignCount, 10);
              if (
                !Number.isFinite(count) ||
                count < 1 ||
                count > assignEntry.matchCount
              ) {
                setAlertText("יש להזין כמות בין 1 ליתרה בבנק");
                setSeverity("warning");
                return;
              }
              assignDateMutation.mutate({
                organizationId: currOrganization.id,
                id: assignEntry.id,
                matchedAt: assignDate.format("YYYY-MM-DD"),
                matchCount: count,
              });
            }}
          >
            שייך
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
